import { supabase } from '@/lib/supabase';
import { getKoreanDate } from '@/lib/dateUtils';
import { getTodayOperatingHours } from '../seller-store';
import { formatTimeToHHMM } from '../seller-helpers';
import { subtractMinutes } from './discount-time';

export interface ProductOffer {
  id: string;
  product_id: string;
  offer_type: 'DEADLINE_DISCOUNT' | 'RESERVATION_DISCOUNT';
  discount_rate: number;
  discounted_price: number;
  deadline_pickup_start_time?: string;
  deadline_pickup_end_time?: string;
  deadline_order_start_time?: string;
  deadline_order_end_time?: string;
  is_active: boolean;
  is_current: boolean;
  valid_from?: string;
  valid_until?: string;
  created_at: string;
  updated_at: string;
}

/**
 * 레스토랑의 product_offers를 조회합니다
 * @param restaurantId - 레스토랑 ID
 * @param offerType - 할인 유형
 * @returns 활성화된 product_offers 목록
 */
export async function getProductOffers(
  restaurantId: string,
  offerType: 'DEADLINE_DISCOUNT' | 'RESERVATION_DISCOUNT',
  options?: { includeInactive?: boolean }
): Promise<{
  success: boolean;
  data?: ProductOffer[];
  error?: string;
}> {
  try {
    const includeInactive = options?.includeInactive === true;
    
    // 먼저 해당 레스토랑의 상품 IDs를 가져오기
    const { data: products, error: productsError } = await supabase
      .from('products')
      .select('id')
      .eq('restaurant_id', restaurantId);
    
    if (productsError) {
      throw productsError;
    }
    
    if (!products || products.length === 0) {
      return {
        success: true,
        data: []
      };
    }
    
    const productIds = products.map(p => p.id);
    
    let query = supabase
      .from('product_offers')
      .select(`
        id,
        product_id,
        offer_type,
        discount_rate,
        discounted_price,
        deadline_pickup_start_time,
        deadline_pickup_end_time,
        deadline_order_start_time,
        deadline_order_end_time,
        is_active,
        is_current,
        valid_from,
        valid_until,
        created_at,
        updated_at
      `)
      .eq('offer_type', offerType)
      .eq('is_current', true)
      .in('product_id', productIds);

    // 설정 화면 프리필 용도로만 비활성(off) 오퍼 포함 여부를 옵션화
    if (!includeInactive) {
      query = query.eq('is_active', true);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Product offers fetch error:', error);
      throw error;
    }

    return {
      success: true,
      data: data || []
    };
  } catch (error) {
    console.error('Get product offers error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'product_offers 조회에 실패했습니다'
    };
  }
}

/**
 * 상품의 할인 설정을 저장하거나 업데이트합니다
 * @param productId - 상품 ID
 * @param offerData - 할인 설정 데이터
 */
export async function upsertProductOffer(
  productId: string,
  offerData: Partial<ProductOffer> & {
    offer_type: 'DEADLINE_DISCOUNT' | 'RESERVATION_DISCOUNT';
    discount_rate: number;
    discounted_price: number;
  }
): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    // 기존 offer 확인
    const { data: existingOffer, error: offerError } = await supabase
      .from('product_offers')
      .select('id')
      .eq('product_id', productId)
      .eq('offer_type', offerData.offer_type)
      .eq('is_current', true)
      .maybeSingle();

    if (offerError && offerError.code !== 'PGRST116') {
      console.error('Error checking existing offer:', offerError);
      throw offerError;
    }

    const now = new Date().toISOString();

    // 마감할인 valid_from: KST 오프셋 포함해 의도한 날짜/시간을 그대로 저장
    if (offerData.offer_type === 'DEADLINE_DISCOUNT') {
      if (!offerData.valid_from) {
        const orderStart = offerData.deadline_order_start_time || '00:00';
        const todayKst = getKoreanDate();
        // UTC 표시에서도 동일 시각으로 보이도록 Z로 저장
        offerData.valid_from = `${todayKst}T${orderStart}:00Z`;
      }
      // 전달된 valid_from이 있으면 그대로 사용
    }

    if (existingOffer) {
      // 기존 offer 업데이트
      const updateData = {
        ...offerData,
        updated_at: now
      };

      const { error: updateError } = await supabase
        .from('product_offers')
        .update(updateData)
        .eq('id', existingOffer.id);

      if (updateError) {
        throw updateError;
      }
    } else {
      // 새 offer 생성
      const insertData = {
        product_id: productId,
        ...offerData,
        is_active: true,
        is_current: true,
        created_at: now,
        updated_at: now
      };

      const { error: insertError } = await supabase
        .from('product_offers')
        .insert(insertData);

      if (insertError) {
        throw insertError;
      }
    }

    return { success: true };
  } catch (error) {
    console.error('Upsert product offer error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '할인 설정 저장에 실패했습니다'
    };
  }
}

/**
 * 상품의 할인 설정을 삭제합니다
 * @param productId - 상품 ID
 * @param offerType - 할인 유형
 */
export async function removeProductOffer(
  productId: string,
  offerType: 'DEADLINE_DISCOUNT' | 'RESERVATION_DISCOUNT'
): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    const { error } = await supabase
      .from('product_offers')
      .delete()
      .eq('product_id', productId)
      .eq('offer_type', offerType)
      .eq('is_current', true);

    if (error) {
      throw error;
    }

    return { success: true };
  } catch (error) {
    console.error('Remove product offer error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '할인 설정 삭제에 실패했습니다'
    };
  }
}

/**
 * 여러 상품의 할인 설정을 일괄 저장합니다
 * @param products - 저장할 상품 목록
 * @param offerType - 할인 유형
 */
export async function batchUpsertProductOffers(
  products: Array<{
    productId: string;
    discountRate: number;
    discountedPrice: number;
    stock?: number;
    pickupStartTime?: string;
    pickupEndTime?: string;
  }>,
  offerType: 'DEADLINE_DISCOUNT' | 'RESERVATION_DISCOUNT',
  restaurantId?: string,
  options?: {
    useCustomOrderWindow?: boolean;
    customOrderStartTime?: string;
    customOrderEndTime?: string;
  }
): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    const useCustomOrderWindow = options?.useCustomOrderWindow === true;
    const customOrderStartTime = options?.customOrderStartTime;
    const customOrderEndTime = options?.customOrderEndTime;
    const resolvedOrderTimes: Array<{ orderStart: string; orderEnd: string }> = [];
    const shouldApplyAlwaysOpen = offerType === 'DEADLINE_DISCOUNT' && !useCustomOrderWindow && Boolean(restaurantId);
    let deadlineAlwaysOpen = false;
    let businessOpenTime: string | null = null;
    let businessCloseTime: string | null = null;
    let isClosedToday = false;

    if (shouldApplyAlwaysOpen && restaurantId) {
      const { data: restaurantConfig, error: restaurantError } = await supabase
        .from('restaurants')
        .select('deadline_always_open')
        .eq('id', restaurantId)
        .single();

      if (restaurantError) {
        console.error('Get restaurant config error:', restaurantError);
        throw restaurantError;
      }

      deadlineAlwaysOpen = restaurantConfig?.deadline_always_open === true;

      if (deadlineAlwaysOpen) {
        const todayHours = await getTodayOperatingHours(restaurantId);
        businessOpenTime = formatTimeToHHMM(todayHours?.open_time ?? null);
        businessCloseTime = formatTimeToHHMM(todayHours?.close_time ?? null);
        isClosedToday = todayHours?.is_closed === true;
      }
    }

    for (const product of products) {
      const offerData: Partial<ProductOffer> & {
        offer_type: 'DEADLINE_DISCOUNT' | 'RESERVATION_DISCOUNT';
        discount_rate: number;
        discounted_price: number;
        max_quantity_per_order?: number | null;
      } = {
        offer_type: offerType,
        discount_rate: product.discountRate,
        discounted_price: product.discountedPrice
      };

      // max_quantity_per_order는 무제한(NULL)으로 설정
      offerData.max_quantity_per_order = null;
      
      // 마감할인의 경우 시간 정보 추가
      if (offerType === 'DEADLINE_DISCOUNT' && product.pickupStartTime && product.pickupEndTime) {
        offerData.deadline_pickup_start_time = product.pickupStartTime;
        offerData.deadline_pickup_end_time = product.pickupEndTime;

        // 커스텀 주문시간이 설정된 매장은 입력값을 그대로 사용, 아니면 기존(수령시작 30분 전~수령종료)
        let orderStartTime = useCustomOrderWindow && customOrderStartTime
          ? customOrderStartTime
          : subtractMinutes(product.pickupStartTime, 30);
        let orderEndTime = useCustomOrderWindow && customOrderEndTime
          ? customOrderEndTime
          : product.pickupEndTime;

        if (deadlineAlwaysOpen && !useCustomOrderWindow) {
          if (isClosedToday) {
            orderStartTime = '23:59';
            orderEndTime = '23:59';
          } else {
            if (businessOpenTime) orderStartTime = businessOpenTime;
            if (businessCloseTime) orderEndTime = businessCloseTime;
          }
        }

        offerData.deadline_order_start_time = orderStartTime;
        offerData.deadline_order_end_time = orderEndTime;
        resolvedOrderTimes.push({ orderStart: orderStartTime, orderEnd: orderEndTime });

        // valid_from 계산 (오늘 날짜 + 주문시작시간, UTC 표기에서도 동일 숫자로 보이도록 Z 사용)
        const todayKst = getKoreanDate();
        offerData.valid_from = `${todayKst}T${orderStartTime}:00Z`;
      } else if (offerType === 'RESERVATION_DISCOUNT') {
        // 예약할인은 날짜와 무관하게 단일 offer로 관리
        // valid_from/until을 undefined로 설정하여 항상 활성화
        offerData.valid_from = undefined;
        offerData.valid_until = undefined;
      }
      
      const result = await upsertProductOffer(product.productId, offerData);
      if (!result.success) {
        throw new Error(result.error);
      }
    }

    // 마감할인의 경우 restaurants 테이블도 동기화
    if (offerType === 'DEADLINE_DISCOUNT' && products.length > 0 && restaurantId) {
      const orderTimes = resolvedOrderTimes.length > 0
        ? resolvedOrderTimes
        : products
          .filter(p => p.pickupStartTime && p.pickupEndTime)
          .map(p => {
            const orderStart = subtractMinutes(p.pickupStartTime!, 30);
            return {
              orderStart,
              orderEnd: p.pickupEndTime!
            };
          });

      if (orderTimes.length > 0) {
        // 시간을 분 단위로 변환하는 함수
        const toMinutes = (timeStr: string): number => {
          const [h, m] = timeStr.split(':').map(Number);
          return h * 60 + m;
        };

        // 가장 이른 주문시작, 가장 늦은 주문종료 찾기 (숫자 비교)
        const earliestStart = orderTimes.reduce((min, time) => 
          toMinutes(time.orderStart) < toMinutes(min) ? time.orderStart : min, 
          orderTimes[0].orderStart
        );
        const latestEnd = orderTimes.reduce((max, time) => 
          toMinutes(time.orderEnd) > toMinutes(max) ? time.orderEnd : max, 
          orderTimes[0].orderEnd
        );

        // restaurants 테이블 업데이트
        const updateData: Record<string, any> = {
          deadline_discount_start_time: earliestStart,
          deadline_discount_end_time: latestEnd,
          use_custom_deadline_order_window: useCustomOrderWindow,
          custom_deadline_order_start_time: useCustomOrderWindow ? (customOrderStartTime ?? earliestStart) : null,
          custom_deadline_order_end_time: useCustomOrderWindow ? (customOrderEndTime ?? latestEnd) : null,
          timeout_exempt: useCustomOrderWindow ? true : false
        };

        const { error: restaurantError } = await supabase
          .from('restaurants')
          .update(updateData)
          .eq('id', restaurantId);

        if (restaurantError) {
          console.error('Restaurant table update error:', restaurantError);
          // 에러가 발생해도 product_offers 저장은 성공했으므로 계속 진행
        }
      }
    }

    return { success: true };
  } catch (error) {
    console.error('Batch upsert product offers error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '일괄 저장에 실패했습니다'
    };
  }
}

/**
 * 특정 상품들의 오퍼 활성 상태를 일괄 변경합니다
 */
export async function setOffersActiveForProducts(
  productIds: string[],
  offerType: 'DEADLINE_DISCOUNT' | 'RESERVATION_DISCOUNT',
  isActive: boolean
): Promise<{ success: boolean; error?: string }> {
  try {
    if (productIds.length === 0) return { success: true };

    const { error } = await supabase
      .from('product_offers')
      .update({
        is_active: isActive,
        updated_at: new Date().toISOString()
      })
      .in('product_id', productIds)
      .eq('offer_type', offerType)
      .eq('is_current', true);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Set offers active state error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : '오퍼 상태 변경에 실패했습니다'
    };
  }
}
